import { Component, Inject } from '@angular/core';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';

import { iDedupItem } from '@app/Shared/interfaces/iDedup';
import { AppState } from '@app/Store';
import { selectColumnConfigForEntity } from '@app/Store/features/column-config.feature';
import { ColumnConfig } from '@app/Store/reducers/column-config.reducer';
import { select, Store } from '@ngrx/store';

export interface DedupPreviewData {
    survivor: iDedupItem;
    merged: iDedupItem[];
    goldenFields: { [key: string]: any };
    goldenKey: number;
    entityName: string;
    idField: string;
    mergingMasterKeys: number[];
}

@Component({
    selector: 'app-dedup-preview',
    templateUrl: './dedup-preview.component.html',
    styleUrls: ['./dedup-preview.component.scss'],
})
export class DedupPreviewComponent {
    public survivor: iDedupItem;
    public merged: iDedupItem[];
    public goldenFields: { [key: string]: any };
    public goldenKey: number;
    public entityName: string;
    public idField: string;
    public mergingMasterKeys: number[];

    // Filter state
    public showOnlyChanged: boolean = false;

    private columnConfig: ColumnConfig[] = [];

    constructor(public dialogRef: MatDialogRef<DedupPreviewComponent>, @Inject(MAT_DIALOG_DATA) public data: DedupPreviewData, private store: Store<AppState>) {
        // Initialize with defensive checks
        this.survivor = data?.survivor || ({} as iDedupItem);
        this.merged = data?.merged || [];
        this.goldenFields = data?.goldenFields || {};
        this.goldenKey = data?.goldenKey || 0;
        this.entityName = data?.entityName || '';
        this.idField = data?.idField || '';
        this.mergingMasterKeys = data?.mergingMasterKeys || [];

        // Ensure survivor has data property
        if (!this.survivor.data) {
            this.survivor.data = {};
        }
    }

    ngOnInit(): void {
        // Load column config for header names
        if (this.entityName) {
            this.store.pipe(select(selectColumnConfigForEntity(this.entityName))).subscribe((config) => {
                this.columnConfig = config || [];
            });
        }
    }

    // Get header name for a field from column config, fallback to field name
    public getHeaderName(field: string): string {
        const config = this.columnConfig.find((col) => col.field === field);
        return config?.headerName || field;
    }

    public onConfirm(): void {
        this.dialogRef.close({ confirmed: true });
    }

    public onCancel(): void {
        this.dialogRef.close({ confirmed: false });
    }

    public getFieldValue(field: string): any {
        return this.goldenFields[field] ?? 'N/A';
    }

    public getSurvivorValue(field: string): any {
        return this.survivor.data?.[field] ?? 'N/A';
    }

    public hasFieldChanged(field: string): boolean {
        const goldenValue = this.goldenFields[field];
        const survivorValue = this.survivor.data?.[field];

        // Normalize for comparison
        const normalize = (val: any) => {
            if (val === null || val === undefined || val === '') return '';
            return String(val).trim().toLowerCase();
        };

        return normalize(goldenValue) !== normalize(survivorValue);
    }

    public getMergedRecordCount(): number {
        return this.merged.length;
    }

    public getTotalFieldsCount(): number {
        return Object.keys(this.goldenFields).length;
    }

    public getChangedFieldsCount(): number {
        return Object.keys(this.goldenFields).filter((field) => this.hasFieldChanged(field)).length;
    }

    public toggleShowOnlyChanged(): void {
        this.showOnlyChanged = !this.showOnlyChanged;
    }

    public getFilteredFields(): string[] {
        const allFields = Object.keys(this.goldenFields);
        if (this.showOnlyChanged) {
            return allFields.filter((field) => this.hasFieldChanged(field));
        }
        return allFields;
    }

    // Expose Object.keys to template
    public Object = Object;
}
